<template>
  <div class="pet-type-page">
    <aside class="pet-type-nav page-card">
      <div class="pet-type-nav__head">
        <div>
          <div class="page-card__title">一级分类</div>
          <div class="page-card__desc">{{ summaryText }}，{{ levelSummaryText }}</div>
        </div>
        <button class="action-button pet-type-compact-button" type="button" @click="createRootType">新增</button>
      </div>

      <div class="pet-type-root-list">
        <button
          type="button"
          class="pet-type-root"
          :class="{ 'pet-type-root--active': currentParentCode === '' && !isSearchMode }"
          @click="selectRoot('')"
        >
          <span class="pet-type-root__mark">全</span>
          <span class="pet-type-root__copy">
            <strong>全部一级</strong>
            <small>{{ rootNodes.length }} 个根分类</small>
          </span>
        </button>

        <button
          v-for="item in rootNodes"
          :key="item.typeCode"
          type="button"
          class="pet-type-root"
          :class="{ 'pet-type-root--active': selectedRootCode === item.typeCode && !isSearchMode }"
          @click="selectRoot(item.typeCode)"
        >
          <span class="pet-type-root__thumb">
            <img v-if="resolvePreviewImageUrl(item.imageUrl)" :src="resolvePreviewImageUrl(item.imageUrl)" alt="" />
            <span v-else>{{ nodeInitial(item) }}</span>
          </span>
          <span class="pet-type-root__copy">
            <strong>{{ item.typeName }}</strong>
            <small>{{ childCount(item) }} 个子级 / 共 {{ descendantCount(item) }} 个</small>
          </span>
        </button>
      </div>
    </aside>

    <section class="pet-type-workbench page-card">
      <div class="pet-type-workbench__head">
        <div>
          <div class="page-card__title">{{ workbenchTitle }}</div>
          <div class="page-card__desc">{{ workbenchDesc }}</div>
        </div>
        <div class="pet-type-workbench__actions">
          <button class="ghost-button" type="button" :disabled="loading" @click="loadTree">刷新</button>
          <button
            class="action-button"
            type="button"
            :disabled="currentParentNode && currentParentNode.levelNo >= 4"
            @click="createInCurrentScope"
          >
            {{ createButtonText }}
          </button>
        </div>
      </div>

      <div class="pet-type-searchbar">
        <input
          v-model.trim="searchKeyword"
          type="text"
          placeholder="搜索名称 / 编码 / 完整路径"
        />
        <button v-if="isSearchMode" class="ghost-button pet-type-clear-button" type="button" @click="clearSearch">清空</button>
      </div>

      <div class="pet-type-breadcrumb">
        <button type="button" @click="goToScope('')">全部一级</button>
        <template v-for="item in breadcrumbNodes">
          <span :key="`${item.typeCode}-sep`">/</span>
          <button :key="item.typeCode" type="button" @click="goToScope(item.typeCode)">
            {{ item.typeName }}
          </button>
        </template>
      </div>

      <div v-if="message.text" class="pet-type-message" :class="message.type === 'error' ? 'pet-type-message--error' : 'pet-type-message--success'">
        {{ message.text }}
      </div>

      <div v-if="loading" class="page-state pet-type-state">加载中...</div>
      <div v-else class="pet-type-table-wrap">
        <table class="table pet-type-table">
          <thead>
            <tr>
              <th>分类</th>
              <th>路径</th>
              <th>层级</th>
              <th>排序</th>
              <th>状态</th>
              <th>百科</th>
              <th>子级</th>
              <th class="sticky-action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in displayNodes"
              :key="item.typeCode"
              :class="{ 'pet-type-table-row--active': editingCode === item.typeCode }"
              @click="selectNode(item)"
            >
              <td>
                <div class="pet-type-name-cell">
                  <div class="pet-type-name-cell__thumb">
                    <img v-if="resolvePreviewImageUrl(item.imageUrl)" :src="resolvePreviewImageUrl(item.imageUrl)" alt="" />
                    <span v-else>{{ nodeInitial(item) }}</span>
                  </div>
                  <div class="pet-type-name-cell__copy">
                    <strong>{{ item.typeName }}</strong>
                    <small>{{ item.typeCode }}</small>
                  </div>
                </div>
              </td>
              <td class="pet-type-path-cell">{{ item.typeNamePath || item.typeName }}</td>
              <td>L{{ item.levelNo || 1 }}</td>
              <td>{{ typeof item.sortNo === "number" ? item.sortNo : "-" }}</td>
              <td>
                <span class="pet-type-status" :class="item.status === 1 ? 'pet-type-status--enabled' : 'pet-type-status--disabled'">
                  {{ item.status === 1 ? "启用" : "停用" }}
                </span>
              </td>
              <td>
                <span class="knowledge-status" :class="knowledgeStatusClass(item)">
                  {{ knowledgeStatusText(item) }}
                </span>
              </td>
              <td>{{ childCount(item) }}</td>
              <td class="sticky-action-column">
                <div class="pet-type-row-actions" @click.stop>
                  <button
                    class="table-action-button table-action-button--ghost"
                    type="button"
                    :disabled="childCount(item) === 0"
                    @click="enterNode(item)"
                  >
                    下级
                  </button>
                  <button
                    class="table-action-button table-action-button--ghost"
                    type="button"
                    :disabled="item.levelNo >= 4"
                    @click="addChild(item)"
                  >
                    新增
                  </button>
                  <button class="table-action-button" type="button" @click="selectNode(item)">编辑</button>
                  <button class="table-action-button table-action-button--ghost" type="button" :disabled="submitting" @click="toggleStatus(item)">
                    {{ item.status === 1 ? "停用" : "启用" }}
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="displayNodes.length === 0">
              <td colspan="8">{{ isSearchMode ? "没有匹配的类型" : "当前层级暂无子分类" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <aside v-if="editorVisible" class="pet-type-editor page-card" :class="{ 'pet-type-editor--create': isCreateMode }">
      <div class="pet-type-editor__head">
        <div>
          <div class="page-card__title">{{ editorTitle }}</div>
          <div class="page-card__desc">{{ editorDesc }}</div>
        </div>
        <button class="modal-close-button" type="button" aria-label="关闭" @click="closeEditor">×</button>
      </div>

      <div class="pet-type-editor__context">
        <div>
          <span>当前父级</span>
          <strong>{{ currentParentLabel }}</strong>
        </div>
        <div>
          <span>目标层级</span>
          <strong>L{{ draft.levelNo || 1 }}</strong>
        </div>
      </div>

      <div v-if="draft.levelNo === 2" class="pet-type-editor-tabs">
        <button type="button" :class="{ active: editorTab === 'catalog' }" @click="editorTab = 'catalog'">分类设置</button>
        <button type="button" :class="{ active: editorTab === 'knowledge' }" @click="editorTab = 'knowledge'">
          百科内容
          <span>{{ knowledgeCompletenessText }}</span>
        </button>
      </div>

      <div class="pet-type-editor__scroll">
        <div v-if="editorTab === 'catalog' || draft.levelNo !== 2" class="pet-type-form">
          <label class="pet-type-field">
            <span>类型编码</span>
            <input
              v-model.trim="draft.typeCode"
              type="text"
              placeholder="例如：cat-ragdoll"
              :disabled="isEditing"
            />
          </label>

          <label class="pet-type-field">
            <span>类型名称</span>
            <input v-model.trim="draft.typeName" type="text" placeholder="例如：布偶" />
          </label>

          <div v-if="!isCreateMode" class="pet-type-form-row">
            <label class="pet-type-field">
              <span>短标签</span>
              <input v-model.trim="draft.shortLabel" type="text" maxlength="16" placeholder="默认首字" />
            </label>
            <label class="pet-type-field">
              <span>排序</span>
              <input v-model.number="draft.sortNo" type="number" min="0" step="10" />
            </label>
          </div>

          <div v-if="!isCreateMode" class="pet-type-form-row">
            <label class="pet-type-field">
              <span>层级</span>
              <select v-model.number="draft.levelNo" @change="syncParentForLevel">
                <option :value="1">一级</option>
                <option :value="2">二级</option>
                <option :value="3">三级</option>
                <option :value="4">四级</option>
              </select>
            </label>
            <label class="pet-type-field">
              <span>状态</span>
              <select v-model.number="draft.status">
                <option :value="1">启用</option>
                <option :value="0">停用</option>
              </select>
            </label>
          </div>

          <label v-if="!isCreateMode" class="pet-type-field">
            <span>父级类型</span>
            <select v-model="draft.parentTypeCode" :disabled="draft.levelNo === 1">
              <option value="">无</option>
              <option
                v-for="item in parentOptions"
                :key="item.typeCode"
                :value="item.typeCode"
              >
                {{ item.typeNamePath }}
              </option>
            </select>
          </label>

          <label v-if="!isCreateMode" class="pet-type-field">
            <span>特征标签</span>
            <input v-model.trim="draft.traitLabel" type="text" placeholder="例如：家猫 / 长毛 / 温顺系" />
          </label>

          <label v-if="showImageField" class="pet-type-field">
            <span>{{ draft.levelNo === 2 ? "分类图 / 百科封面" : "分类图" }}</span>
            <input v-model.trim="draft.imageUrl" type="text" placeholder="/uploads/theme-icons/xxx.png" />
            <div class="pet-type-upload-card">
              <div class="pet-type-upload-preview">
                <img v-if="previewImageUrl" :src="previewImageUrl" alt="" />
                <span v-else>{{ draft.shortLabel || draft.typeName.slice(0, 1) || "图" }}</span>
              </div>
              <label class="pet-type-upload-button">
                上传图片
                <input type="file" accept="image/*" @change="uploadCurrentTypeImage" />
              </label>
            </div>
          </label>

          <label v-if="!isCreateMode" class="pet-type-field">
            <span>类型说明</span>
            <textarea v-model.trim="draft.description" rows="4" placeholder="说明这个类型的用途、特点和分类位置。" />
          </label>
        </div>

        <div v-else class="pet-type-form knowledge-form">
          <div class="knowledge-publish-row">
            <div>
              <strong>小程序百科展示</strong>
              <small>草稿不会在小程序百科出现，资料确认后再发布。</small>
            </div>
            <select v-model.number="draft.knowledge.status">
              <option :value="0">保存为草稿</option>
              <option :value="1">发布到小程序</option>
            </select>
          </div>

          <div class="pet-type-form-row">
            <label class="pet-type-field">
              <span>百科标题</span>
              <input v-model.trim="draft.knowledge.title" type="text" :placeholder="draft.typeName || '例如：豹纹守宫'" />
            </label>
            <label class="pet-type-field">
              <span>学名</span>
              <input v-model.trim="draft.knowledge.scientificName" type="text" placeholder="例如：Eublepharis macularius" />
            </label>
          </div>

          <label class="pet-type-field">
            <span>别名 / 常见品系</span>
            <input v-model.trim="draft.knowledge.aliasName" type="text" placeholder="多个名称用顿号或逗号分隔" />
          </label>

          <label class="pet-type-field">
            <span>百科简介</span>
            <textarea v-model.trim="draft.knowledge.summary" rows="5" placeholder="概括原产地、食性、市场热度、保护级别和风险提示。" />
          </label>

          <div class="pet-type-form-row knowledge-metric-grid">
            <label class="pet-type-field">
              <span>适宜温度</span>
              <input v-model.trim="draft.knowledge.temperatureText" type="text" placeholder="25-32℃" />
            </label>
            <label class="pet-type-field">
              <span>适宜湿度</span>
              <input v-model.trim="draft.knowledge.humidityText" type="text" placeholder="40-50%" />
            </label>
            <label class="pet-type-field">
              <span>活动时段</span>
              <select v-model="draft.knowledge.activityPeriod">
                <option value="">未填写</option>
                <option value="白天">白天</option>
                <option value="夜间">夜间</option>
                <option value="晨昏">晨昏</option>
              </select>
            </label>
            <label class="pet-type-field">
              <span>栖息类型</span>
              <input v-model.trim="draft.knowledge.habitatText" type="text" placeholder="地栖 / 树栖 / 水栖" />
            </label>
            <label class="pet-type-field">
              <span>食性</span>
              <input v-model.trim="draft.knowledge.dietType" type="text" placeholder="虫食 / 杂食" />
            </label>
            <label class="pet-type-field">
              <span>饲养难度</span>
              <select v-model="draft.knowledge.difficultyLevel">
                <option value="">未填写</option>
                <option value="入门">入门</option>
                <option value="进阶">进阶</option>
                <option value="专业">专业</option>
              </select>
            </label>
          </div>

          <div class="pet-type-form-row">
            <label class="pet-type-field">
              <span>性格特点</span>
              <input v-model.trim="draft.knowledge.temperament" type="text" placeholder="温顺、夜行、胆小" />
            </label>
            <label class="pet-type-field">
              <span>寿命</span>
              <input v-model.trim="draft.knowledge.lifespanText" type="text" placeholder="人工饲养约 10-15 年" />
            </label>
          </div>

          <label class="pet-type-field">
            <span>成体尺寸</span>
            <input v-model.trim="draft.knowledge.adultSizeText" type="text" placeholder="体长、体重等成体信息" />
          </label>

          <label class="pet-type-field">
            <span>喂食建议</span>
            <textarea v-model.trim="draft.knowledge.feedingGuide" rows="4" placeholder="主食、频率、营养补充和禁忌。" />
          </label>
          <label class="pet-type-field">
            <span>环境建议</span>
            <textarea v-model.trim="draft.knowledge.environmentGuide" rows="4" placeholder="饲养箱、温湿度、垫材、躲避和光照。" />
          </label>
          <label class="pet-type-field">
            <span>健康提醒</span>
            <textarea v-model.trim="draft.knowledge.healthTips" rows="4" placeholder="常见疾病、异常表现和日常检查。" />
          </label>
          <label class="pet-type-field">
            <span>繁育提示</span>
            <textarea v-model.trim="draft.knowledge.breedingTips" rows="4" placeholder="性成熟、繁殖期、产卵和孵化条件。" />
          </label>
          <label class="pet-type-field">
            <span>搜索标签</span>
            <input v-model.trim="draft.knowledge.tags" type="text" placeholder="守宫,入门,虫食,热门" />
          </label>
        </div>
      </div>

      <div class="pet-type-editor__foot" :class="{ 'pet-type-editor__foot--create': isCreateMode }">
        <button class="ghost-button" type="button" @click="resetDraft">{{ isCreateMode ? "清空" : "重置" }}</button>
        <button v-if="!isCreateMode" class="ghost-button" type="button" :disabled="!canDelete || submitting" @click="removeCurrentType">删除</button>
        <button class="action-button" type="button" :disabled="submitting" @click="saveCurrentType">
          {{ submitting ? "保存中..." : (isCreateMode ? "创建" : "保存") }}
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import {
  createPetType,
  deletePetType,
  fetchPetTypeDetail,
  fetchPetTypeTree,
  uploadPetTypeImage,
  updatePetType
} from "../api/petType"
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"

function createEmptyDraft() {
  return {
    typeCode: "",
    parentTypeCode: "",
    typeName: "",
    shortLabel: "",
    levelNo: 1,
    description: "",
    traitLabel: "",
    imageUrl: "",
    sortNo: 10,
    status: 1,
    knowledge: createEmptyKnowledgeDraft()
  }
}

function createEmptyKnowledgeDraft(typeName = "") {
  return {
    title: typeName,
    aliasName: "",
    scientificName: "",
    summary: "",
    temperatureText: "",
    humidityText: "",
    activityPeriod: "",
    habitatText: "",
    dietType: "",
    difficultyLevel: "",
    temperament: "",
    lifespanText: "",
    adultSizeText: "",
    feedingGuide: "",
    environmentGuide: "",
    healthTips: "",
    breedingTips: "",
    tags: "",
    status: 0
  }
}

function toKnowledgePayload(knowledge, typeName) {
  const draft = knowledge || createEmptyKnowledgeDraft(typeName)
  return {
    title: draft.title || typeName,
    aliasName: draft.aliasName,
    scientificName: draft.scientificName,
    summary: draft.summary,
    temperatureText: draft.temperatureText,
    humidityText: draft.humidityText,
    activityPeriod: draft.activityPeriod,
    habitatText: draft.habitatText,
    dietType: draft.dietType,
    difficultyLevel: draft.difficultyLevel,
    temperament: draft.temperament,
    lifespanText: draft.lifespanText,
    adultSizeText: draft.adultSizeText,
    feedingGuide: draft.feedingGuide,
    environmentGuide: draft.environmentGuide,
    healthTips: draft.healthTips,
    breedingTips: draft.breedingTips,
    tags: draft.tags,
    status: typeof draft.status === "number" ? draft.status : 0
  }
}

export default {
  name: "PetTypeCatalogView",
  data() {
    return {
      loading: false,
      submitting: false,
      tree: [],
      flatNodes: [],
      totalNodeCount: 0,
      maxLevelNo: 0,
      currentParentCode: "",
      searchKeyword: "",
      editorVisible: false,
      editorTab: "catalog",
      draft: createEmptyDraft(),
      editingCode: "",
      message: {
        type: "",
        text: ""
      }
    }
  },
  computed: {
    isEditing() {
      return this.editingCode.length > 0
    },
    isCreateMode() {
      return !this.isEditing
    },
    isSearchMode() {
      return this.searchKeyword.trim().length > 0
    },
    summaryText() {
      return `共 ${this.totalNodeCount} 个类型节点`
    },
    levelSummaryText() {
      return `最大层级 L${this.maxLevelNo || 1}`
    },
    rootNodes() {
      return this.tree || []
    },
    currentParentNode() {
      if (!this.currentParentCode) {
        return null
      }
      return this.flatNodes.find(item => item.typeCode === this.currentParentCode) || null
    },
    selectedRootCode() {
      if (!this.currentParentCode) {
        return ""
      }
      const node = this.currentParentNode
      if (!node || !node.typeCodePath) {
        return this.currentParentCode
      }
      return node.typeCodePath.split("/").filter(Boolean)[0] || this.currentParentCode
    },
    currentChildren() {
      if (!this.currentParentNode) {
        return this.rootNodes
      }
      return Array.isArray(this.currentParentNode.children) ? this.currentParentNode.children : []
    },
    normalizedSearchKeyword() {
      return this.searchKeyword.trim().toLowerCase()
    },
    searchResults() {
      if (!this.normalizedSearchKeyword) {
        return []
      }
      return this.flatNodes.filter(item => {
        const text = [
          item.typeName,
          item.typeCode,
          item.typeNamePath,
          item.typeCodePath,
          item.traitLabel,
          item.knowledgeTitle
        ].filter(Boolean).join(" ").toLowerCase()
        return text.includes(this.normalizedSearchKeyword)
      })
    },
    displayNodes() {
      return this.isSearchMode ? this.searchResults : this.currentChildren
    },
    breadcrumbNodes() {
      const node = this.currentParentNode
      if (!node || !node.typeCodePath) {
        return []
      }
      return node.typeCodePath
        .split("/")
        .filter(Boolean)
        .map(code => this.flatNodes.find(item => item.typeCode === code))
        .filter(Boolean)
    },
    workbenchTitle() {
      if (this.isSearchMode) {
        return "搜索结果"
      }
      if (!this.currentParentNode) {
        return "一级分类"
      }
      return `${this.currentParentNode.typeName} 的子分类`
    },
    workbenchDesc() {
      if (this.isSearchMode) {
        return `匹配 ${this.searchResults.length} 个类型，点击“编辑”或“下级”继续操作。`
      }
      if (!this.currentParentNode) {
        return "查看和维护顶层分类，进入某个分类后可继续管理下级。"
      }
      return `当前路径：${this.currentParentNode.typeNamePath || this.currentParentNode.typeName}`
    },
    createButtonText() {
      if (this.currentParentNode) {
        return this.currentParentNode.levelNo >= 4 ? "已到四级" : "新增子级"
      }
      return "新增一级"
    },
    currentParentLabel() {
      const parent = this.flatNodes.find(item => item.typeCode === this.draft.parentTypeCode)
      if (parent) {
        return parent.typeNamePath || parent.typeName
      }
      return "无"
    },
    editorTitle() {
      return this.isEditing ? "编辑类型" : "新增类型"
    },
    editorDesc() {
      if (!this.isCreateMode) {
        return this.previewPathText
      }
      const parent = this.flatNodes.find(item => item.typeCode === this.draft.parentTypeCode)
      return parent ? `${parent.typeNamePath} / 新增子级` : "一级分类"
    },
    showImageField() {
      return this.draft.levelNo <= 2
    },
    parentOptions() {
      const expectedParentLevel = this.draft.levelNo - 1
      return this.flatNodes.filter(item => {
        if (expectedParentLevel <= 0) {
          return false
        }
        if (item.levelNo !== expectedParentLevel) {
          return false
        }
        if (this.isEditing && item.typeCode === this.editingCode) {
          return false
        }
        if (this.isEditing && item.typeCodePath && item.typeCodePath.indexOf(`${this.editingCode}/`) === 0) {
          return false
        }
        return true
      })
    },
    canDelete() {
      return this.isEditing
    },
    previewPathText() {
      const parent = this.flatNodes.find(item => item.typeCode === this.draft.parentTypeCode)
      const currentName = this.draft.typeName || "未命名类型"
      if (this.draft.levelNo === 1 || !parent) {
        return currentName
      }
      return `${parent.typeNamePath} / ${currentName}`
    },
    previewImageUrl() {
      return this.resolvePreviewImageUrl(this.draft.imageUrl)
    },
    knowledgeCompleteness() {
      const knowledge = this.draft.knowledge || createEmptyKnowledgeDraft()
      const fields = [
        this.draft.imageUrl,
        knowledge.title,
        knowledge.summary,
        knowledge.temperatureText,
        knowledge.humidityText,
        knowledge.dietType,
        knowledge.difficultyLevel,
        knowledge.feedingGuide,
        knowledge.environmentGuide,
        knowledge.healthTips
      ]
      return fields.filter(value => String(value || "").trim().length > 0).length * 10
    },
    knowledgeCompletenessText() {
      return `${this.knowledgeCompleteness}%`
    }
  },
  created() {
    this.loadTree()
  },
  methods: {
    async loadTree() {
      this.loading = true
      this.clearMessage()
      try {
        const res = await fetchPetTypeTree()
        const data = res.data || {}
        this.tree = data.items || []
        this.totalNodeCount = data.totalNodeCount || 0
        this.maxLevelNo = data.maxLevelNo || 0
        this.flatNodes = this.flattenTree(this.tree)
        this.syncCurrentScope()
        this.syncEditingDraft()
      } catch (error) {
        this.showError(error)
      } finally {
        this.loading = false
      }
    },
    flattenTree(nodes, result = []) {
      nodes.forEach(item => {
        result.push(item)
        if (item.children && item.children.length > 0) {
          this.flattenTree(item.children, result)
        }
      })
      return result
    },
    syncCurrentScope() {
      if (!this.currentParentCode) {
        return
      }
      const exists = this.flatNodes.some(item => item.typeCode === this.currentParentCode)
      if (!exists) {
        this.currentParentCode = ""
      }
    },
    syncEditingDraft() {
      if (!this.isEditing) {
        return
      }
      const current = this.flatNodes.find(item => item.typeCode === this.editingCode)
      if (current) {
        this.applyNodeToDraft(current, this.draft.knowledge)
        return
      }
      this.editingCode = ""
      this.draft = createEmptyDraft()
    },
    selectRoot(typeCode) {
      this.searchKeyword = ""
      this.currentParentCode = typeCode || ""
      this.clearMessage()
    },
    goToScope(typeCode) {
      this.searchKeyword = ""
      this.currentParentCode = typeCode || ""
      this.clearMessage()
    },
    enterNode(node) {
      if (!node || node.levelNo >= 4 || this.childCount(node) === 0) {
        return
      }
      this.searchKeyword = ""
      this.currentParentCode = node.typeCode
      this.clearMessage()
    },
    clearSearch() {
      this.searchKeyword = ""
    },
    async selectNode(node) {
      if (!node) {
        return
      }
      this.editingCode = node.typeCode || ""
      this.applyNodeToDraft(node)
      this.editorVisible = true
      this.editorTab = "catalog"
      this.clearMessage()
      const requestedCode = this.editingCode
      try {
        const res = await fetchPetTypeDetail(requestedCode)
        if (this.editingCode !== requestedCode) return
        this.applyNodeToDraft(res.data || node)
      } catch (error) {
        if (this.editingCode === requestedCode) this.showError(error)
      }
    },
    applyNodeToDraft(node, preservedKnowledge = null) {
      const knowledge = node.knowledge || preservedKnowledge || createEmptyKnowledgeDraft(node.typeName || "")
      this.draft = {
        typeCode: node.typeCode || "",
        parentTypeCode: node.parentTypeCode || "",
        typeName: node.typeName || "",
        shortLabel: node.shortLabel || "",
        levelNo: node.levelNo || 1,
        description: node.description || "",
        traitLabel: node.traitLabel || "",
        imageUrl: node.imageUrl || "",
        sortNo: typeof node.sortNo === "number" ? node.sortNo : 10,
        status: typeof node.status === "number" ? node.status : 1,
        knowledge: {
          ...createEmptyKnowledgeDraft(node.typeName || ""),
          ...knowledge,
          status: typeof knowledge.status === "number" ? knowledge.status : 0
        }
      }
    },
    createRootType() {
      this.searchKeyword = ""
      this.currentParentCode = ""
      this.editingCode = ""
      this.draft = createEmptyDraft()
      this.editorVisible = true
      this.editorTab = "catalog"
      this.draft.levelNo = 1
      this.draft.parentTypeCode = ""
      this.draft.sortNo = this.resolveNextSortNo("")
      this.clearMessage()
    },
    createInCurrentScope() {
      if (this.currentParentNode) {
        this.addChild(this.currentParentNode)
        return
      }
      this.createRootType()
    },
    addChild(node) {
      if (!node || node.levelNo >= 4) {
        return
      }
      this.searchKeyword = ""
      this.currentParentCode = node.typeCode
      this.editingCode = ""
      this.draft = createEmptyDraft()
      this.editorVisible = true
      this.editorTab = "catalog"
      this.draft.parentTypeCode = node.typeCode
      this.draft.levelNo = (node.levelNo || 1) + 1
      this.draft.sortNo = this.resolveNextSortNo(node.typeCode)
      this.draft.status = 1
      this.clearMessage()
    },
    resolveNextSortNo(parentTypeCode) {
      const siblings = this.flatNodes.filter(item => (item.parentTypeCode || "") === (parentTypeCode || ""))
      if (siblings.length === 0) {
        return 10
      }
      const maxSort = siblings.reduce((result, item) => {
        const current = typeof item.sortNo === "number" ? item.sortNo : 0
        return current > result ? current : result
      }, 0)
      return maxSort + 10
    },
    syncParentForLevel() {
      if (this.draft.levelNo === 1) {
        this.draft.parentTypeCode = ""
        return
      }
      const matchedParent = this.parentOptions.find(item => item.typeCode === this.draft.parentTypeCode)
      if (!matchedParent) {
        this.draft.parentTypeCode = ""
      }
    },
    resetDraft() {
      if (this.isEditing) {
        const current = this.flatNodes.find(item => item.typeCode === this.editingCode)
        if (current) {
          this.selectNode(current)
          return
        }
      }
      if (this.currentParentNode && this.currentParentNode.levelNo < 4) {
        this.addChild(this.currentParentNode)
        return
      }
      this.createRootType()
    },
    async toggleStatus(node) {
      if (!node || !node.typeCode) {
        return
      }
      const payload = this.nodeToPayload(node)
      payload.status = node.status === 1 ? 0 : 1
      this.submitting = true
      this.clearMessage()
      try {
        await updatePetType(node.typeCode, payload)
        this.message = { type: "success", text: payload.status === 1 ? "类型已启用" : "类型已停用" }
        this.editingCode = node.typeCode
        await this.loadTree()
      } catch (error) {
        this.showError(error)
      } finally {
        this.submitting = false
      }
    },
    nodeToPayload(node) {
      return {
        typeCode: node.typeCode,
        parentTypeCode: node.levelNo === 1 ? "" : node.parentTypeCode,
        typeName: node.typeName,
        shortLabel: node.shortLabel,
        levelNo: node.levelNo,
        description: node.description,
        traitLabel: node.traitLabel,
        imageUrl: node.imageUrl,
        sortNo: typeof node.sortNo === "number" ? node.sortNo : 10,
        status: typeof node.status === "number" ? node.status : 1
      }
    },
    async saveCurrentType() {
      if (!this.draft.typeCode) {
        this.message = { type: "error", text: "请先填写类型编码" }
        return
      }
      if (!this.draft.typeName) {
        this.message = { type: "error", text: "请先填写类型名称" }
        return
      }
      if (this.draft.levelNo > 1 && !this.draft.parentTypeCode) {
        this.message = { type: "error", text: "当前层级必须选择父级类型" }
        return
      }
      this.submitting = true
      this.clearMessage()
      const payload = {
        typeCode: this.draft.typeCode,
        parentTypeCode: this.draft.levelNo === 1 ? "" : this.draft.parentTypeCode,
        typeName: this.draft.typeName,
        shortLabel: this.draft.shortLabel,
        levelNo: this.draft.levelNo,
        description: this.draft.description,
        traitLabel: this.draft.traitLabel,
        imageUrl: this.draft.imageUrl,
        sortNo: this.draft.sortNo,
        status: this.draft.status
      }
      if (this.draft.levelNo === 2) {
        payload.knowledge = toKnowledgePayload(this.draft.knowledge, this.draft.typeName)
      }
      try {
        if (this.isEditing) {
          await updatePetType(this.editingCode, payload)
          this.message = { type: "success", text: "类型已更新" }
        } else {
          await createPetType(payload)
          this.editingCode = payload.typeCode
          this.message = { type: "success", text: "类型已创建" }
        }
        this.currentParentCode = payload.parentTypeCode || ""
        this.editingCode = payload.typeCode
        await this.loadTree()
      } catch (error) {
        this.showError(error)
      } finally {
        this.submitting = false
      }
    },
    async removeCurrentType() {
      if (!this.canDelete || !this.editingCode) {
        return
      }
      if (!window.confirm(`确认删除类型“${this.draft.typeName || this.editingCode}”吗？`)) {
        return
      }
      const nextParentCode = this.draft.parentTypeCode || ""
      this.submitting = true
      this.clearMessage()
      try {
        await deletePetType(this.editingCode)
        this.message = { type: "success", text: "类型已删除" }
        this.editingCode = ""
        this.currentParentCode = nextParentCode
        this.draft = createEmptyDraft()
        this.editorVisible = false
        await this.loadTree()
      } catch (error) {
        this.showError(error)
      } finally {
        this.submitting = false
      }
    },
    async uploadCurrentTypeImage(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null
      if (!file) return
      try {
        const res = await uploadPetTypeImage(file)
        const url = res.data && res.data.url ? res.data.url : ""
        if (!url) return
        this.draft.imageUrl = url
        this.message = { type: "success", text: "分类图已上传，记得保存类型" }
      } catch (error) {
        this.showError(error)
      } finally {
        if (event && event.target) {
          event.target.value = ""
        }
      }
    },
    childCount(node) {
      return node && Array.isArray(node.children) ? node.children.length : 0
    },
    knowledgeStatusText(node) {
      if (!node || node.levelNo === 1) return "-"
      if (!node.knowledgeId) return node.levelNo === 2 ? "待补充" : "未关联"
      if (node.levelNo > 2 && node.knowledgeOwnerTypeCode !== node.typeCode) {
        return `继承 ${node.knowledgeTitle || "百科"}`
      }
      const percent = typeof node.knowledgeCompletenessPercent === "number" ? node.knowledgeCompletenessPercent : 0
      return node.knowledgeStatus === 1 ? `已发布 ${percent}%` : `草稿 ${percent}%`
    },
    knowledgeStatusClass(node) {
      if (!node || !node.knowledgeId) return "knowledge-status--empty"
      if (node.levelNo > 2 && node.knowledgeOwnerTypeCode !== node.typeCode) return "knowledge-status--inherited"
      return node.knowledgeStatus === 1 ? "knowledge-status--published" : "knowledge-status--draft"
    },
    descendantCount(node) {
      if (!node || !Array.isArray(node.children) || node.children.length === 0) {
        return 0
      }
      return node.children.reduce((total, child) => total + 1 + this.descendantCount(child), 0)
    },
    nodeInitial(node) {
      const text = node && (node.shortLabel || node.typeName) ? (node.shortLabel || node.typeName) : "类"
      return text.slice(0, 1).toUpperCase()
    },
    closeEditor() {
      this.editorVisible = false
    },
    clearMessage() {
      this.message = {
        type: "",
        text: ""
      }
    },
    showError(error) {
      this.message = {
        type: "error",
        text: error && error.message ? error.message : "操作失败"
      }
    },
    resolvePreviewImageUrl(value) {
      return resolveBackendAssetUrl(value)
    }
  }
}
</script>

<style scoped>
.pet-type-page {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 16px;
  min-height: calc(100vh - 150px);
}

.pet-type-nav,
.pet-type-workbench {
  min-height: 0;
  height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pet-type-editor {
  position: fixed;
  top: 76px;
  right: 28px;
  bottom: 24px;
  z-index: 80;
  width: min(760px, calc(100vw - 56px));
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
}

.pet-type-editor--create {
  bottom: auto;
  max-height: calc(100vh - 100px);
}

.pet-type-editor--create .pet-type-editor__scroll {
  flex: 0 1 auto;
}

.pet-type-nav__head,
.pet-type-workbench__head,
.pet-type-editor__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.modal-close-button {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: 1px solid #d7deea;
  border-radius: 10px;
  background: #ffffff;
  color: #475569;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.pet-type-compact-button {
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
}

.pet-type-root-list {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 2px;
}

.pet-type-root {
  width: 100%;
  min-height: 58px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid #e5edf6;
  border-radius: 14px;
  background: #ffffff;
  color: #334155;
  text-align: left;
  cursor: pointer;
}

.pet-type-root--active {
  border-color: #2563eb;
  background: #eef4ff;
  color: #1d4ed8;
}

.pet-type-root__mark,
.pet-type-root__thumb {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
  background: #eef4ff;
  color: #2563eb;
  font-weight: 800;
}

.pet-type-root__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pet-type-root__copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.pet-type-root__copy strong {
  overflow: hidden;
  color: inherit;
  font-size: 14px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pet-type-root__copy small {
  overflow: hidden;
  color: #64748b;
  font-size: 12px;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pet-type-workbench__actions {
  display: flex;
  flex-shrink: 0;
  gap: 10px;
}

.pet-type-searchbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.pet-type-searchbar input {
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #d7deea;
  border-radius: 12px;
  background: #ffffff;
  color: #111827;
}

.pet-type-clear-button {
  height: 40px;
  white-space: nowrap;
}

.pet-type-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  margin-bottom: 12px;
  overflow-x: auto;
  color: #94a3b8;
  white-space: nowrap;
}

.pet-type-breadcrumb button {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 13px;
  cursor: pointer;
}

.pet-type-message {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 13px;
}

.pet-type-message--success {
  background: #eefbf3;
  border: 1px solid #c9ecd5;
  color: #166534;
}

.pet-type-message--error {
  background: #fff4f4;
  border: 1px solid #f4cccc;
  color: #b91c1c;
}

.pet-type-state {
  flex: 1;
}

.pet-type-table-wrap {
  min-height: 0;
  flex: 1;
  overflow: auto;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #ffffff;
}

.pet-type-table {
  min-width: 1020px;
}

.pet-type-table th,
.pet-type-table td {
  vertical-align: middle;
}

.pet-type-table th:nth-child(1),
.pet-type-table td:nth-child(1) {
  width: 210px;
}

.pet-type-table th:nth-child(2),
.pet-type-table td:nth-child(2) {
  width: 210px;
}

.pet-type-table th:nth-child(3),
.pet-type-table td:nth-child(3),
.pet-type-table th:nth-child(4),
.pet-type-table td:nth-child(4),
.pet-type-table th:nth-child(5),
.pet-type-table td:nth-child(5),
.pet-type-table th:nth-child(7),
.pet-type-table td:nth-child(7) {
  width: 72px;
}

.pet-type-table th:nth-child(6),
.pet-type-table td:nth-child(6) {
  width: 150px;
}

.pet-type-table th:nth-child(8),
.pet-type-table td:nth-child(8) {
  width: 240px;
}

.pet-type-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.pet-type-table .sticky-action-column {
  position: sticky;
  right: 0;
  z-index: 3;
  width: 240px;
  min-width: 240px;
  max-width: 240px;
  background: #ffffff;
  box-shadow: -10px 0 18px rgba(15, 23, 42, 0.08);
  white-space: nowrap;
}

.pet-type-table thead .sticky-action-column {
  z-index: 5;
  background: #f8fafc;
}

.pet-type-table-row--active td {
  background: #f8fbff;
}

.pet-type-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.pet-type-name-cell__thumb {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
  background: #eef4ff;
  color: #2563eb;
  font-weight: 800;
}

.pet-type-name-cell__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pet-type-name-cell__copy {
  min-width: 0;
}

.pet-type-name-cell__copy strong,
.pet-type-name-cell__copy small,
.pet-type-path-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pet-type-name-cell__copy strong {
  display: block;
  color: #111827;
  font-size: 14px;
  line-height: 18px;
}

.pet-type-name-cell__copy small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 16px;
}

.pet-type-path-cell {
  max-width: 210px;
  color: #64748b;
}

.pet-type-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.pet-type-status--enabled {
  background: #eefbf3;
  color: #166534;
}

.pet-type-status--disabled {
  background: #f1f5f9;
  color: #64748b;
}

.knowledge-status {
  display: inline-flex;
  align-items: center;
  max-width: 140px;
  min-height: 24px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 16px;
  white-space: normal;
}

.knowledge-status--published {
  background: #e8f7ef;
  color: #166534;
}

.knowledge-status--draft {
  background: #fff7e6;
  color: #9a6700;
}

.knowledge-status--inherited {
  background: #eef4ff;
  color: #1d4ed8;
}

.knowledge-status--empty {
  background: #f1f5f9;
  color: #64748b;
}

.pet-type-row-actions {
  width: 216px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.table-action-button {
  height: 30px;
  padding: 0 8px;
  border: 1px solid #2563eb;
  border-radius: 9px;
  background: #2563eb;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.table-action-button--ghost {
  border-color: #d7deea;
  background: #ffffff;
  color: #334155;
}

.table-action-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pet-type-editor__context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.pet-type-editor__context div {
  min-width: 0;
  padding: 10px;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  background: #f8fafc;
}

.pet-type-editor__context span {
  display: block;
  color: #64748b;
  font-size: 12px;
  line-height: 16px;
}

.pet-type-editor__context strong {
  display: block;
  margin-top: 5px;
  overflow: hidden;
  color: #111827;
  font-size: 13px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pet-type-editor-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  margin-bottom: 14px;
  padding: 4px;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
  background: #f1f5f9;
}

.pet-type-editor-tabs button {
  min-height: 38px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.pet-type-editor-tabs button.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.pet-type-editor-tabs button span {
  margin-left: 6px;
  color: #2563eb;
}

.pet-type-editor__scroll {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding-right: 2px;
}

.pet-type-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pet-type-form-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.knowledge-metric-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.knowledge-publish-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e8edf3;
}

.knowledge-publish-row div {
  min-width: 0;
}

.knowledge-publish-row strong,
.knowledge-publish-row small {
  display: block;
}

.knowledge-publish-row strong {
  color: #111827;
  font-size: 14px;
}

.knowledge-publish-row small {
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  line-height: 18px;
}

.knowledge-publish-row select {
  width: 150px;
  flex-shrink: 0;
  border: 1px solid #d7deea;
  border-radius: 8px;
  padding: 9px 10px;
  background: #ffffff;
  color: #111827;
}

.pet-type-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pet-type-field span {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.pet-type-field input,
.pet-type-field select,
.pet-type-field textarea {
  width: 100%;
  border: 1px solid #d7deea;
  border-radius: 12px;
  padding: 10px 12px;
  background: #ffffff;
  color: #111827;
}

.pet-type-field textarea {
  resize: vertical;
}

.pet-type-upload-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: #f8fafc;
}

.pet-type-upload-preview {
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 14px;
  background: #eef4ff;
  color: #2563eb;
  font-weight: 800;
}

.pet-type-upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.pet-type-upload-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  background: #0f172a;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  overflow: hidden;
}

.pet-type-upload-button input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.pet-type-editor__foot {
  display: grid;
  grid-template-columns: 1fr 1fr 1.2fr;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #eef2f7;
}

.pet-type-editor__foot--create {
  grid-template-columns: 1fr 1.4fr;
}

@media (max-width: 1320px) {
  .pet-type-page {
    grid-template-columns: 210px minmax(0, 1fr);
  }
}

@media (max-width: 1120px) {
  .pet-type-page {
    grid-template-columns: 1fr;
  }

  .pet-type-nav,
  .pet-type-workbench {
    height: auto;
    min-height: 0;
  }

  .pet-type-editor {
    top: 76px;
    right: 12px;
    bottom: 12px;
    width: min(760px, calc(100vw - 24px));
  }

  .pet-type-root-list {
    max-height: 260px;
  }

  .pet-type-table-wrap {
    max-height: 520px;
  }

  .pet-type-editor__scroll {
    max-height: 620px;
  }
}

@media (max-width: 720px) {
  .pet-type-workbench__head,
  .pet-type-nav__head {
    flex-direction: column;
  }

  .pet-type-workbench__actions,
  .pet-type-searchbar,
  .pet-type-editor__foot {
    width: 100%;
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .pet-type-form-row,
  .knowledge-metric-grid,
  .pet-type-editor__context {
    grid-template-columns: 1fr;
  }

  .knowledge-publish-row {
    align-items: stretch;
    flex-direction: column;
  }

  .knowledge-publish-row select {
    width: 100%;
  }
}
</style>
